import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Fab,
  Divider,
  createTheme,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Add,
  Edit,
  Delete,
  NavigateNext,
  Close,
  Clear,
  DirectionsBoat,
  Schedule,
  AccessTime,
  Person,
  Visibility,
  EventNote,
  AddCircle,
} from "@mui/icons-material";
import moment from "moment";
import "moment/locale/en-gb"; // Use en-gb for DD/MM format
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
// Types
interface BoatShow {
  id: string;
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  description?: string;
}

interface BoatShowSchedule {
  id: string;
  boatShow?: BoatShow;
  location: string;
  startDate: string;
  endDate: string;
  defaultMaxAppointments: number;
  nbrTest: number;
  startTime: string;
  frequency: number;
  model: any; // in minutes
}

interface Prospect {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}

interface Appointment {
  id: string;
  scheduleId: string;
  prospect: Prospect;
  appointmentDate: string; // ISO string
  notes?: string;
  numberOfPeople: number;
}

const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    primary: { main: "#C4B08C" },
    error: { main: "#d32f2f" },
    warning: { main: "#ed6c02" },
    success: { main: "#2e7d32" },
    text: { primary: "#111111", secondary: "#555555" },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});
const API_BASE_URL = process.env.REACT_APP_API_URL;

moment.locale("en-gb");

const BoatShowAppointmentPage: React.FC = () => {
  const [boatShow, setBoatShow] = useState<any | null>(null);

  const [schedule, setSchedule] = useState<BoatShowSchedule | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPeopleCountError, setIsPeopleCountError] = useState(false);

  // States pour les fenêtres modales
  const [isAppointmentDetailsDialogOpen, setIsAppointmentDetailsDialogOpen] =
    useState(false);
  const [isAddAppointmentDialogOpen, setIsAddAppointmentDialogOpen] =
    useState(false);
  const [isEditAppointmentDialogOpen, setIsEditAppointmentDialogOpen] =
    useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddProspectDialogOpen, setIsAddProspectDialogOpen] = useState(false);

  // States pour la gestion des rendez-vous
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);

  const [newAppointmentData, setNewAppointmentData] = useState({
    prospectId: "",
    numberOfPeople: 1,
    notes: "",
  });

  const [newProspectData, setNewProspectData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });

  const [editedAppointmentData, setEditedAppointmentData] = useState({
    numberOfPeople: 1,
    notes: "",
    appointmentDate: "",
  });

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const location = useLocation();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const boatShowId = query.get('idshow');
  const scheduleId = query.get('idboatshow-schedules');

      const name=query.get('model');


  const fetchAllData = async () => {
    if (!boatShowId || !scheduleId) {
      setError("Boat show or schedule IDs are missing.");
      setLoading(false);
      return;
    }

    try {
      const [scheduleRes, boatShowRes, appointmentsRes, prospectsRes] =
        await Promise.all([
          axios.get<BoatShowSchedule>(
            `${API_BASE_URL}/boatshow-schedules/${scheduleId}`
          ),
          axios.get<any>(`${API_BASE_URL}/boatshows/${boatShowId}`),
          axios.get<Appointment[]>(
            `${API_BASE_URL}/boatshow-appointments/schedule/${scheduleId}`
          ),
          axios.get<Prospect[]>(`${API_BASE_URL}/prospects`),
        ]);

      setSchedule(scheduleRes.data);
      setBoatShow(boatShowRes.data);
      const name = scheduleRes.data.model?.modele;
      console.log(name);

      setAppointments(appointmentsRes.data);
      setProspects(prospectsRes.data);
      setError(null);
    } catch (err) {
      toast.error("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [boatShowId, scheduleId]);

  const generateTimeSlots = (
    startTime: string,
    frequency: number,
    nbrTest: number
  ) => {
    const slots = [];
    const startMoment = moment(startTime, "HH:mm");
    for (let i = 0; i < nbrTest; i++) {
      const slotStart = startMoment.clone().add(i * frequency, "minutes");
      const slotEnd = slotStart.clone().add(frequency, "minutes");
      slots.push({
        start: slotStart.format("HH:mm"),
        end: slotEnd.format("HH:mm"),
      });
    }
    return slots;
  };

  const timeSlots = useMemo(() => {
    if (!schedule) return [];
    return generateTimeSlots(
      schedule.startTime,
      schedule.frequency,
      schedule.nbrTest
    );
  }, [schedule]);

  const getCalendarData = useMemo(() => {
    if (!schedule || !boatShow) return [];
    const calendarData = [];
    const start = moment(schedule.startDate);
    const end = moment(schedule.endDate);

    if (!start.isValid() || !end.isValid()) {
      console.error("The boat show dates are not valid.");
      return [];
    }

    const duration = end.diff(start, "days") + 1; // +1 to include the end date

    for (let i = 0; i < duration; i++) {
      const dayDate = start.clone().add(i, "days");
      const dayData = {
        date: dayDate.toDate(),
        slots: timeSlots.map((timeSlot) => {
          const slotDateTime = moment(
            dayDate.format("YYYY-MM-DD") + "T" + timeSlot.start
          );
          const appointmentsInSlot = appointments.filter((appt) =>
            moment(appt.appointmentDate).isSame(slotDateTime, "minute")
          );
          const booked = appointmentsInSlot.reduce(
            (total, appt) => total + (appt.numberOfPeople || 0),
            0
          );
          const remaining = schedule.defaultMaxAppointments - booked;
          const percentage =
            schedule.defaultMaxAppointments > 0
              ? (booked / schedule.defaultMaxAppointments) * 100
              : 0;

          // Déterminer la couleur de l'indicateur ET de la bordure
          let indicatorColor = "#4caf50"; // Green par défaut
          let borderColor = "rgba(224, 224, 224, 1)"; // Gris par défaut pour les bordures

          if (percentage >= 75) {
            indicatorColor = "#f44336"; // Red
            borderColor = "rgba(196, 176, 140, 0.5)"; // Beige pour les créneaux occupés
          } else if (percentage >= 50) {
            indicatorColor = "#ff9800"; // Orange
            borderColor = "rgba(196, 176, 140, 0.5)"; // Beige pour les créneaux occupés
          } else if (percentage === 0) {
            indicatorColor = "#4caf50"; // Green pour les créneaux vides (0%)
            borderColor = "rgba(76, 175, 80, 0.5)"; // Vert pour les créneaux vides
          }

          return {
            time: timeSlot,
            appointments: appointmentsInSlot,
            booked,
            remaining,
            percentage,
            indicatorColor, // <-- Utilisé pour la barre de progression
            borderColor, // <-- Nouveau : utilisé pour les bordures
          };
        }),
      };
      calendarData.push(dayData);
    }
    return calendarData;
  }, [schedule, boatShow, appointments, timeSlots]);

  const handleSlotClick = (dayData: any, slotData: any) => {
    setSelectedSlot({ ...slotData, dayData });
    setIsAppointmentDetailsDialogOpen(true);
  };

  const handleCloseAppointmentDetailsDialog = () => {
    setIsAppointmentDetailsDialogOpen(false);
    setSelectedSlot(null);
  };

  const handleOpenAddAppointment = () => {
    setIsPeopleCountError(false);
    setIsAppointmentDetailsDialogOpen(false);
    setIsAddAppointmentDialogOpen(true);
  };

  const handleCloseAddAppointmentDialog = () => {
    setIsAddAppointmentDialogOpen(false);
    setNewAppointmentData({
      prospectId: "",
      numberOfPeople: 1,
      notes: "",
    });
    setIsPeopleCountError(false);
  };

  const handleOpenEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setEditedAppointmentData({
      numberOfPeople: appointment.numberOfPeople,
      notes: appointment.notes || "",
      appointmentDate: appointment.appointmentDate,
    });
    setIsPeopleCountError(false);
    setIsAppointmentDetailsDialogOpen(false);
    setIsEditAppointmentDialogOpen(true);
  };

  const handleCloseEditAppointmentDialog = () => {
    setIsEditAppointmentDialogOpen(false);
    setSelectedAppointment(null);
    setIsPeopleCountError(false);
  };

  const handleOpenDeleteDialog = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentDetailsDialogOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedAppointment(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const intValue = parseInt(value, 10);

    if (name === "numberOfPeople") {
      const remaining = selectedSlot?.remaining || 0;
      if (intValue > remaining) {
        setIsPeopleCountError(true);
      } else {
        setIsPeopleCountError(false);
      }
    }

    if (isAddAppointmentDialogOpen) {
      setNewAppointmentData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (isEditAppointmentDialogOpen) {
      setEditedAppointmentData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setNewAppointmentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProspectInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewProspectData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveAppointment = async () => {
    if (!selectedSlot || !newAppointmentData.prospectId || isPeopleCountError) {
      toast.error("Please select a prospect or correct the number of people.");

      return;
    }

    const appointmentTime =
      moment(selectedSlot.dayData.date).format("YYYY-MM-DD") +
      "T" +
      selectedSlot.time.start;

    const newAppt = {
      scheduleId,
      prospectId: newAppointmentData.prospectId,
      appointmentTime,
      numberOfPeople: newAppointmentData.numberOfPeople,
      notes: newAppointmentData.notes,
    };

    try {
      await axios.post(`${API_BASE_URL}/boatshow-appointments`, newAppt);
      await fetchAllData();
      handleCloseAddAppointmentDialog();
    } catch (err: any) {
      toast.error(
        "Error saving the appointment," +
          "This appointment slot is fully booked. Please select another time slot."
      );
      await fetchAllData();
    }
  };

  const handleSaveProspect = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/prospects`,
        newProspectData
      );
      setProspects([...prospects, response.data]);
      setNewProspectData({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
      });
      setIsAddProspectDialogOpen(false);
    } catch (err) {
      toast.error("Error saving the prospect.");
    }
  };

  const handleUpdateAppointment = async () => {
    if (!selectedAppointment || isPeopleCountError) return;

    try {
      await axios.put(
        `${API_BASE_URL}/boatshow-appointments/${selectedAppointment.id}`,
        {
          ...editedAppointmentData,
          appointmentTime: editedAppointmentData.appointmentDate,
        }
      );
      await fetchAllData();
      handleCloseEditAppointmentDialog();
    } catch (err) {
      toast.error(
        "This appointment slot is fully booked. Please select another time slot."
      );

      await fetchAllData();
    }
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/boatshow-appointments/${selectedAppointment.id}`
      );
      await fetchAllData();
      handleCloseDeleteDialog();
    } catch (err) {
      toast.error("Error deleting the appointment.");
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return "#f44336"; // Red
    if (percentage >= 50) return "#ff9800"; // Orange
    return "#4caf50"; // Green
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ToastContainer
  position="top-right"
  autoClose={5000}
  hideProgressBar={false}
  newestOnTop={false}
  closeOnClick
  rtl={false}
  pauseOnFocusLoss
  draggable
  pauseOnHover
/>
      <Box mb={6} textAlign="center">
        <Typography
            variant="overline"
            sx={{ 
              color: '#C4B08C', 
              fontWeight: 800, 
              letterSpacing: 2,
              fontSize: '0.9rem' 
            }}
          >
            APPOINTMENTS
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              mb: 1,
              fontFamily: 'Playfair Display, serif',
            }}
          >
            {boatShow?.name || 'Loading...'}
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
          >
            Manage appointments for Model <strong>{name || 'N/A'}</strong> at {schedule?.location}
          </Typography>
      </Box>

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
            }}
          >
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(196, 176, 140, 0.1)', color: '#C4B08C' }}>
              <Schedule />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Schedule Period</Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {moment(schedule?.startDate).format('MMM DD')} - {moment(schedule?.endDate).format('MMM DD, YYYY')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
            }}
          >
             <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(196, 176, 140, 0.1)', color: '#C4B08C' }}>
              <AccessTime />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Slot Configuration</Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                Start: {schedule?.startTime} • {schedule?.frequency} min intervals
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
           <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
            }}
          >
             <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(196, 176, 140, 0.1)', color: '#C4B08C' }}>
              <EventNote />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Capacity</Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                Max {schedule?.defaultMaxAppointments} appts per slot
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 1 : 3, 
            borderRadius: 4, 
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            overflow: 'hidden'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              Appointment Calendar
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<NavigateNext />}
              href="/boatshow-schedules"
              sx={{ borderColor: '#C4B08C', color: '#C4B08C', '&:hover': { borderColor: '#b39d77', bgcolor: 'rgba(196, 176, 140, 0.05)' } }}
            >
              Back to Schedules
            </Button>
          </Box>
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer>
              <Table sx={{ minWidth: isMobile ? 800 : "auto" }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        borderRight: '1px solid rgba(0,0,0,0.05)',
                        pr: 2,
                        width: isMobile ? '120px' : 'auto',
                        color: '#555'
                      }}
                    >
                      Date
                    </TableCell>
                    {timeSlots.map((slot, index) => (
                      <TableCell 
                        key={slot.start} 
                        sx={{ 
                          fontWeight: 700, 
                          textAlign: 'center',
                          borderRight: index < timeSlots.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                          position: 'relative',
                          pr: 1,
                          pl: 1,
                          width: isMobile ? '100px' : '120px',
                          color: '#555'
                        }}
                      >
                        {slot.start} - {slot.end}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getCalendarData.map((dayData, index) => (
                    <TableRow key={index} hover>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          pr: 1,
                          pl: 1,
                          width: isMobile ? "100px" : "120px",
                          height: isMobile ? "80px" : "100px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}
                          >
                            {moment(dayData.date).format("ddd")}
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontSize: isMobile ? "1rem" : "1.2rem" }}
                          >
                            {moment(dayData.date).format("DD")}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: isMobile ? "0.7rem" : "0.8rem" }}
                          >
                            {moment(dayData.date).format("MMM")}
                          </Typography>
                        </Box>
                      </TableCell>
                      {dayData.slots.map((slot: any, slotIndex: number) => (
                        <TableCell
                          key={slot.time.start}
                          onClick={() => handleSlotClick(dayData, slot)}
                          sx={{
                            cursor: "pointer",
                            textAlign: "center",
                            bgcolor:
                              slot.booked > 0
                                ? "rgba(196, 176, 140, 0.1)"
                                : "inherit",
                            "&:hover": { bgcolor: "rgba(196, 176, 140, 0.2)" },
                            // Utiliser la couleur de bordure calculée
                            borderRight:
                              slotIndex < dayData.slots.length - 1
                                ? `1px solid ${slot.borderColor}`
                                : "none",
                            position: "relative",
                            pr: 1,
                            pl: 1,
                            width: isMobile ? "100px" : "120px",
                            height: isMobile ? "80px" : "100px",
                            verticalAlign: "top",
                          }}
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            pt={0.5}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 0.5,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{
                                  color:
                                    slot.booked > 0
                                      ? "#C4B08C"
                                      : "text.secondary",
                                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                                }}
                              >
                                {slot.booked}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                                }}
                              >
                                /
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                                }}
                              >
                                {schedule?.defaultMaxAppointments}
                              </Typography>
                            </Box>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.75rem",
                                mb: 0.5,
                              }}
                            >
                              {slot.remaining} left
                            </Typography>

                            <Box
                              sx={{
                                width: "100%",
                                height: "6px",
                                bgcolor: "#e0e0e0",
                                borderRadius: "3px",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${slot.percentage}%`,
                                  height: "100%",
                                  bgcolor: slot.indicatorColor,
                                  borderRadius: "5px",
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        {/* Dialog for a specific time slot */}
        <Dialog
          open={isAppointmentDetailsDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseAppointmentDetailsDialog();
            }
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Appointments for{" "}
            {selectedSlot?.dayData?.date &&
              moment(selectedSlot.dayData.date).format("LL")}{" "}
            at {selectedSlot?.time.start} - {selectedSlot?.time.end}
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: "400px" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Total Appointments: {selectedSlot?.booked} /{" "}
                {schedule?.defaultMaxAppointments}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenAddAppointment}
                sx={{ bgcolor: "#C4B08C", color: "white" }}
                disabled={selectedSlot?.remaining <= 0}
              >
                Add Appointment
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {selectedSlot?.appointments.length > 0 ? (
              selectedSlot.appointments.map((appointment: Appointment) => (
                <Paper
                  key={appointment.id}
                  elevation={1}
                  sx={{ p: 2, mb: 2, borderRadius: 2 }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {appointment.prospect.firstName}{" "}
                      {appointment.prospect.lastName}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditAppointment(appointment)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(appointment)}
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Email: {appointment.prospect.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mobile: {appointment.prospect.mobile}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Number of People: {appointment.numberOfPeople}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notes: {appointment.notes || "N/A"}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No appointments for this slot.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAppointmentDetailsDialog}
              sx={{ color: "#111" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog to add a new appointment */}
        <Dialog
          open={isAddAppointmentDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseAddAppointmentDialog();
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Add New Appointment
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" mb={2}>
              Selected Slot:{" "}
              {selectedSlot?.dayData?.date &&
                moment(selectedSlot.dayData.date).format("LL")}{" "}
              at {selectedSlot?.time.start} - {selectedSlot?.time.end}
            </Typography>

            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="prospect-select-label">
                  Select a Prospect
                </InputLabel>
                <Select
                  labelId="prospect-select-label"
                  value={newAppointmentData.prospectId}
                  label="Select a Prospect"
                  onChange={handleSelectChange}
                  name="prospectId"
                >
                  {prospects.map((prospect) => (
                    <MenuItem key={prospect.id} value={prospect.id}>
                      {prospect.firstName} {prospect.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                onClick={() => (window.location.href = "/prospects")}
                sx={{
                  mt: isMobile ? 2 : 3,
                  bgcolor: "#C4B08C",
                  color: "white",
                  "&:hover": { bgcolor: "#b39d77" },
                }}
              >
                <AddCircle />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              label="Number of People"
              type="number"
              margin="normal"
              name="numberOfPeople"
              value={newAppointmentData.numberOfPeople}
              onChange={handleInputChange}
              inputProps={{
                min: 1,
                max: selectedSlot?.remaining,
              }}
              error={isPeopleCountError}
              helperText={
                isPeopleCountError
                  ? `Cannot exceed ${selectedSlot?.remaining} people.`
                  : ""
              }
            />
            <TextField
              fullWidth
              label="Notes"
              margin="normal"
              multiline
              rows={3}
              name="notes"
              value={newAppointmentData.notes}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAddAppointmentDialog}
              sx={{ color: "#111" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAppointment}
              sx={{ bgcolor: "#C4B08C", color: "white" }}
              disabled={isPeopleCountError}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog to add a new prospect */}
        <Dialog
          open={isAddProspectDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setIsAddProspectDialogOpen(false);
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add New Prospect</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap={2}
              >
                <Box flex={1}>
                  <TextField
                    fullWidth
                    label="First Name"
                    margin="normal"
                    name="firstName"
                    value={newProspectData.firstName}
                    onChange={handleProspectInputChange}
                    required
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    margin="normal"
                    name="lastName"
                    value={newProspectData.lastName}
                    onChange={handleProspectInputChange}
                    required
                  />
                </Box>
              </Box>
              <TextField
                fullWidth
                label="Email"
                margin="normal"
                name="email"
                value={newProspectData.email}
                onChange={handleProspectInputChange}
                type="email"
              />
              <TextField
                fullWidth
                label="Mobile"
                margin="normal"
                name="mobile"
                value={newProspectData.mobile}
                onChange={handleProspectInputChange}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsAddProspectDialogOpen(false)}
              sx={{ color: "#111" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProspect}
              sx={{ bgcolor: "#C4B08C", color: "white" }}
            >
              Save Prospect
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog to edit an existing appointment */}
        <Dialog
          open={isEditAppointmentDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseEditAppointmentDialog();
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Edit Appointment</DialogTitle>
          <DialogContent dividers>
            {selectedAppointment && (
              <Box mb={2}>
                <Typography variant="body1" mb={1}>
                  Prospect:{" "}
                  <strong>
                    {selectedAppointment.prospect.firstName}{" "}
                    {selectedAppointment.prospect.lastName}
                  </strong>
                </Typography>
              </Box>
            )}
            <Box mb={2}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Appointment Date"
                  value={moment(editedAppointmentData.appointmentDate)}
                  onChange={(newValue) => {
                    if (newValue) {
                      setEditedAppointmentData({
                        ...editedAppointmentData,
                        appointmentDate: newValue.toISOString(),
                      });
                    }
                  }}
                  minDate={
                    schedule?.startDate ? moment(schedule.startDate) : undefined
                  }
                  maxDate={
                    schedule?.endDate
                      ? moment(schedule.endDate).subtract(1, "day")
                      : undefined
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
              <FormControl fullWidth margin="normal">
                <InputLabel id="time-select-label">Appointment Time</InputLabel>
                <Select
                  labelId="time-select-label"
                  value={moment(editedAppointmentData.appointmentDate).format(
                    "HH:mm"
                  )}
                  label="Appointment Time"
                  onChange={(e) => {
                    const selectedTime = e.target.value;
                    const newDateTime =
                      moment(editedAppointmentData.appointmentDate).format(
                        "YYYY-MM-DD"
                      ) +
                      "T" +
                      selectedTime;
                    setEditedAppointmentData({
                      ...editedAppointmentData,
                      appointmentDate: newDateTime,
                    });
                  }}
                >
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot.start} value={slot.start}>
                      {slot.start} - {slot.end}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="Number of People"
              type="number"
              margin="normal"
              name="numberOfPeople"
              value={editedAppointmentData.numberOfPeople}
              onChange={handleInputChange}
              inputProps={{ min: 1 }}
              error={isPeopleCountError}
              helperText={
                isPeopleCountError
                  ? `Cannot exceed the number of available slots.`
                  : ""
              }
            />
            <TextField
              fullWidth
              label="Notes"
              margin="normal"
              multiline
              rows={3}
              name="notes"
              value={editedAppointmentData.notes}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseEditAppointmentDialog}
              sx={{ color: "#111" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateAppointment}
              sx={{ bgcolor: "#C4B08C", color: "white" }}
              disabled={isPeopleCountError}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog to confirm deletion */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleCloseDeleteDialog();
            }
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the appointment for{" "}
              <Typography component="span" fontWeight="bold">
                {selectedAppointment?.prospect?.firstName}{" "}
                {selectedAppointment?.prospect?.lastName}
              </Typography>
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteAppointment}
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Floating button for mobile */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setIsAppointmentDetailsDialogOpen(true)}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              bgcolor: "#C4B08C",
              "&:hover": { bgcolor: "#b39d77" },
            }}
          >
            <Add />
          </Fab>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default BoatShowAppointmentPage;
